import type { LayerId } from "../game/board/types";
import type { DogmaScaffold } from "../game/combat/dogma";
import type { LieSpawnSkin } from "../game/cards/types";

/** How tangled a Doubt is when it spawns (ignored for non-doubt lies). */
export type DoubtScaffold = "core" | "half" | "full";

export type { DogmaScaffold };

export interface SpawnEvent {
  at: number;
  lieId: string;
  /** Default mind */
  layer?: LayerId;
  /** Wave banner / telegraph (not the on-card claim) */
  label?: string;
  /**
   * Campaign / scenario narrative skin: themed name, claim ladder, art plate id.
   * Mechanics stay on `lieId`; presentation uses the skin.
   */
  skin?: LieSpawnSkin;
  /**
   * Doubt only: core = bare question (Redeemable, no timer);
   * half = one outer + timer; full = full onion + timer (default).
   * Wrong Word on core/half can still grow toward max outers.
   */
  doubtScaffold?: DoubtScaffold;
  /** Dogma only: teach = 3 peels; full = Wisdom bookends (4). */
  dogmaScaffold?: DogmaScaffold;
}

/** Hold = survive until duration (campaign). Endless = timer counts up; only integrity ends the run. */
export type ScenarioMode = "hold" | "endless";

export interface ScenarioDef {
  id: string;
  name: string;
  /**
   * Hold missions: seconds until victory if integrity holds.
   * Endless: ignored for win (kept for stats/UI as 0).
   */
  duration: number;
  handSize: number;
  maxIntegrity: number;
  spawns: SpawnEvent[];
  /**
   * When true, residual (I) lies do not twist into Confusion.
   * Use on early teaching missions so unfinished chips stay soft.
   */
  disableConfusion?: boolean;
  /** Default `hold`. Free play uses `endless`. */
  mode?: ScenarioMode;
  /**
   * Endless only: after the last scripted spawn, rotate adversary strategies
   * with rising pressure. Omit to stop spawning when the script ends.
   */
  escalation?: EscalationConfig;
}

/** One weighted lie choice inside an adversary strategy. */
export interface EscalationSpawnChoice {
  lieId: string;
  layer?: LayerId;
  weight: number;
  /** Doubt only — see SpawnEvent.doubtScaffold */
  doubtScaffold?: DoubtScaffold;
  /** Dogma only — see SpawnEvent.dogmaScaffold */
  dogmaScaffold?: DogmaScaffold;
  /**
   * Land on the layer with the most Prayers (ties keep `layer` / Mind).
   * Used by keep-hunters so the wall is the target, not an empty row.
   */
  huntPrayers?: boolean;
}

/**
 * Named pressure protocol after the scripted open.
 * Intervals shrink over time via Game escalation math.
 */
export interface EscalationStrategy {
  id: string;
  /** HUD / wave label */
  label: string;
  /** Seconds this strategy stays active before rotating */
  duration: number;
  /** Base seconds between spawns (scaled down as the run ages) */
  baseInterval: number;
  pool: EscalationSpawnChoice[];
}

export interface EscalationConfig {
  /** Quiet beat after the last scripted `at` before strategies begin */
  leadIn?: number;
  strategies: EscalationStrategy[];
}

/** Take Every Thought — ~2 min; late board forces breaches, not walls. */
export const TAKE_EVERY_THOUGHT: ScenarioDef = {
  id: "take-every-thought",
  name: "Take Every Thought",
  duration: 120,
  handSize: 5,
  maxIntegrity: 80,
  /** Dense enough that single-target alone swarms; layer chips are the relief valve. */
  spawns: [
    { at: 2.5, lieId: "whisper", label: "First whispers" },
    { at: 8, lieId: "whisper" },
    { at: 15, lieId: "heavy-gloom", label: "Gloom settles" },
    { at: 22, lieId: "wildfire" },
    { at: 28, lieId: "whisper", layer: "heart", label: "Bypass thought" },
    { at: 34, lieId: "whisper" },
    { at: 40, lieId: "alone", label: "Isolation" },
    { at: 46, lieId: "whisper" },
    {
      at: 49,
      lieId: "nagging-question",
      layer: "mind",
      label: "Did He really say…?",
      /** Full onion mid-run — player should know peel/Redeem by then */
      doubtScaffold: "full",
    },
    { at: 52, lieId: "anchor-weight", layer: "heart", label: "Heart spike" },
    { at: 58, lieId: "wildfire" },
    { at: 64, lieId: "heavy-gloom" },
    { at: 70, lieId: "whisper", layer: "heart" },
    { at: 76, lieId: "alone", label: "Compounded" },
    { at: 82, lieId: "wildfire", layer: "heart" },
    { at: 88, lieId: "anchor-weight", layer: "heart" },
    { at: 94, lieId: "heavy-gloom", layer: "will", label: "Will under fire" },
    { at: 100, lieId: "whisper", layer: "heart" },
    { at: 106, lieId: "alone", layer: "will" },
    { at: 111, lieId: "wildfire" },
    { at: 116, lieId: "anchor-weight", label: "Final weight" },
  ],
};
